import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { EmailForm } from 'Components/UI/EmailForm';
import LoadingOverlay from 'Components/UI/LoadingOverlay/LoadingOverlay';
import useUserContext from 'Context/UserContext';
import { displayErrorMessage, displaySuccess } from 'Helpers/FacetNotificationManager';
import { render } from 'Helpers/template';
import useDebounce from 'Hooks/useDebounce';
import useCollection from 'Hooks/useCollection';
import useResource from 'Hooks/useResource';

const styles = theme => ({
  root: {
    display: 'flex',
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    width: theme.dimensions.sm,
    padding: theme.spacing(1, 2),
    marginTop: theme.spacing(2),
    overflowY: 'auto',
  },
  actionItemList: {
    paddingTop: theme.spacing(2),
    width: '100%',
  },
  actionButton: {
    margin: theme.spacing(0.5, 0),
    padding: theme.spacing(1, 2),
    fontWeight: theme.typography.fontWeightBold,
  },
  paraplannerSelect: {
    width: '100%',
  },
  taskContainer: {
    margin: theme.spacing(2, 0),
    width: '100%',
  },
  personalizedMessageInput: {
    marginTop: theme.spacing(2),
  },
  emailForm: {
    marginRight: theme.spacing(2),
    flexGrow: 1,
  },
});

const EmailViewOnlyEditor = ({
  className,
  classes,
  meetingResource,
  meetingTemplateCollection,
  onSaveMeetingTemplate,
  meetingAgenda,
  setMeetingAgenda,
  handleClose,
  autosave,
  showSaveButton,
  ...rest
}) => {
  const { userResource, agentResource } = useUserContext();
  const {
    resource: meetingAgendaResourceCopy,
    setResourceDeprecated: setMeetingAgendaResourceCopy,
  } = useResource(meetingAgenda);

  const [subject, setSubject] = useState(meetingAgenda?.attr('subject') || '');
  const [content, setContent] = useState(meetingAgenda?.attr('content') || '');

  const [selectedTemplateId, setSelectedTemplateId] = useState();

  const {
    collection: attachments,
    setCollection: setAttachments,
  } = useCollection();

  const [saved, setSaved] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);

  const getSelectedTemplateById = () => meetingTemplateCollection
    .resources()
    .list()
    .find(resource => resource.id() === selectedTemplateId);

  const getSelectedTemplate = () => {
    const template = getSelectedTemplateById();
    if (!template) {
      return meetingTemplateCollection.create();
    }
    return template;
  };

  useEffect(() => {
    if (!selectedTemplateId) {
      return;
    }
    const template = getSelectedTemplate();
    if (template) {
      const date = moment(meetingResource.attr('startTime')).format(
        'MMMM Do, YYYY',
      );
      const time = moment(meetingResource.attr('startTime')).format(
        'h:mma',
      );
      setContent(
        render(template.attr('content'), {
          eventDate: `${date} at ${time}`,
          agentName: userResource.attr('firstName'),
        }),
      );
      setSubject(template.attr('subject'));
    }
  }, [selectedTemplateId]);
  // End Hooks

  // Handles inserting templates via the templateOptionsMenu
  const handleInsertTemplate = (template) => {
    if (!template) return;
    const date = moment(meetingResource.attr('startTime')).format(
      'MMMM Do, YYYY',
    );
    const time = moment(meetingResource.attr('startTime')).format(
      'h:mma',
    );
    setContent(
      render(template.attr('content'), {
        eventDate: `${date} at ${time}`,
        agentName: userResource.attr('firstName'),
      }),
    );
    setSubject(template.attr('subject'));
  };

  const saveTemplate = async (template) => {
    let meetingTemplate = template;
    // If it's a Facet template or the user hasn't selected a template, then we need to make a new version of the template
    if (meetingTemplate.id() && !meetingTemplate.attr('agentId')) {
      meetingTemplate = meetingTemplateCollection
        .create()
        .attr('name', meetingTemplate.attr('name'));
    }
    try {
      await meetingTemplate
        .attr('subject', subject)
        .attr('content', content)
        .attr('agentId', agentResource.id())
        .submit();
      onSaveMeetingTemplate();
      displaySuccess('Successfully saved template');
    } catch (err) {
      if (err.status === 409) {
        displayErrorMessage(err, 'A template with this name already exists');
      } else {
        displayErrorMessage(err, 'Failed to save meeting template');
      }
    }
  };

  const refreshAttachments = () => {
    if (meetingResource) {
      meetingResource
        .relationship('attachments')
        .link('related')
        .Get()
        .then((coll) => {
          setAttachments(coll);
        });
    }
  };

  useEffect(() => {
    refreshAttachments();
  }, []);

  const onScheduledDateChange = (updatedResource) => {
    setMeetingAgendaResourceCopy(updatedResource);
    setMeetingAgenda(updatedResource);
  };

  // eslint-disable-next-line arrow-body-style
  const handleSaveMeetingAgenda = async (resource, isLiveEmail) => {
    try {
      const meetingAgendaResponse = await resource.submit();
      const [savedMeetingAgendaResource] = meetingAgendaResponse;

      setMeetingAgendaResourceCopy(savedMeetingAgendaResource.copy());
      setMeetingAgenda(savedMeetingAgendaResource.copy());

      // If the email is a live one then send update subject/content to reset it
      if (isLiveEmail) {
        handleClose(null, true);
      }

      setSaved(true);
    } catch (err) {
      const message = Array.isArray(err?.errors)
        ? err.errors[0]?.detail
        : 'No detail error message';

      displayErrorMessage(err, message);
    }
  };

  const handleSendClientEmail = async () => {
    setSendingEmail(true);
    await handleSaveMeetingAgenda(meetingAgendaResourceCopy.copy()
      .attr('scheduledSendType', null)
      .attr('scheduledSendCustomDate', null)
      .attr('sendAt', null)
      .attr('sendEmail', 'live-email'), true);
    setSendingEmail(false);
  };

  const applyAutoSave = useDebounce((sub, con) => {
    if (autosave && meetingAgendaResourceCopy) {
      return handleSaveMeetingAgenda(meetingAgendaResourceCopy.copy().attributes({ subject: sub, content: con }), false);
    }

    return Promise.resolve();
  });

  return (
    <div
      className={classNames(classes.root, className)}
    >
      {sendingEmail && <LoadingOverlay />}
      <EmailForm
        {...rest}
        className={classes.emailForm}
        body={content}
        subject={subject}
        attachments={attachments}
        templateOptionsPrompt="Template"
        showSaveButton={showSaveButton}
        templateOptions={meetingTemplateCollection
          .resources()
          .list()
          .map(template => ({
            value: template.id(),
            name: template.attr('name'),
            group: template.attr('templateType'),
          }))}
        showSaveEmailTemplate
        selectedTemplate={getSelectedTemplate()}
        selectedTemplateId={getSelectedTemplate().id()}
        isValidTemplateName={v => !meetingTemplateCollection
          .resources()
          .list()
          .find(
            template => template.attr('agentId') && template.attr('name') === v,
          )
        }
        onChangeBody={(v) => {
          // onChangeBody is tricky because it does not get called when a user typed, but rather
          // asynchronously once CKEditor has processed the change.
          // IMPORTANT: in the case of saving a meeting the content will match what CKEditor thinks it is
          // and in this case we do NOT want to save again.
          if (v === content) {
            return;
          }

          // Otherwise, content and v don't match so we should save the value
          setSaved(false);
          setContent(v);
          applyAutoSave(subject, v);
        }}
        onChangeSubject={(v) => {
          setSaved(false);
          setSubject(v);
          applyAutoSave(v, content);
        }}
        onChangeTemplate={setSelectedTemplateId}
        onSaveMeetingTemplate={saveTemplate}
        onAttachmentChange={refreshAttachments}
        meeting={meetingResource}
        meetingAgendaResource={meetingAgendaResourceCopy}
        agenda={meetingAgenda}
        onSave={() => {
          return handleSaveMeetingAgenda(
            meetingAgendaResourceCopy.copy().attributes({ content, subject }),
            false,
          );
        }}
        onScheduleSendEmail={() => {
          return handleSaveMeetingAgenda(
            meetingAgendaResourceCopy.copy().attributes({ content, subject }),
            true,
          );
        }}
        saved={saved}
        onSendClientEmail={handleSendClientEmail}
        showPreview={false}
        updateMeetingTemplates={onSaveMeetingTemplate}
        onScheduledDateChange={onScheduledDateChange}
        onInsertTemplate={handleInsertTemplate}
      />
    </div>
  );
};

EmailViewOnlyEditor.defaultProps = {
  className: '',
  autosave: true,
  showSaveButton: true,
};

EmailViewOnlyEditor.propTypes = {
  meetingTemplateCollection: PropTypes.object.isRequired,
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  meetingResource: PropTypes.object.isRequired,
  meetingAgenda: PropTypes.object.isRequired,
  onSaveMeetingTemplate: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  setMeetingAgenda: PropTypes.func.isRequired,
  autosave: PropTypes.bool,
  showSaveButton: PropTypes.bool,

};

export default withStyles(styles)(EmailViewOnlyEditor);
